import { difference, get, omit, pick, cloneDeep } from 'lodash'
import moment from 'moment'
import isUnderwriter from './is-underwriter'
import { 
  DEFAULT_ADDITIONAL_COVERAGE_OPTIONS as oiDefaultCoverages,
  DEFAULT_FEES as defaultOIFees
} from '../submissions/components/OIPricing'
import { DEFAULT_ADDITIONAL_COVERAGE_OPTIONS as ocpDefaultCoverages } from '../submissions/components/OCPPricing'
import { BASE_COMMISSION } from '../constants'

const userActionsConstants = {
  ADDL_COVG_LENDER_CHANGED: 'ADDL_COVG_LENDER_CHANGED',
  LIMITS_CHANGED: 'LIMITS_CHANGED',
  COMMISSION_CHANGED: 'COMMISSION_CHANGED',
}

exports.userActionsConstants = userActionsConstants

/**
 * Return the Default Coverage
 *
 * @param {Object} submission
 * @param {Object} defaultPreference Optional
 * @param {Boolean} isNewSubmission Optional
 * @param {String} type Optional
 * @returns {} Object
 */
exports.setDefaultCoverage = (
  submission = {},
  defaultPreference = null,
  defaultAdditionalCoverages = [],
  defaultOptionalCoverages = [],
  isNewSubmission = false,
  type = null,
) => {
  let defaultCoverages
  const { projectAddress = {} } = submission
  const { projectState = '' } = projectAddress
  const skipDefaultCoverage = !!(
    defaultPreference &&
    defaultPreference[type] &&
    !defaultPreference[type]['skipPreferencesAlert']
  )

  if (skipDefaultCoverage) {
    var { base_coverage: base, additional_coverages } = defaultPreference[type]
  }

  if (
    isNewSubmission &&
    skipDefaultCoverage &&
    type === 'oi' &&
    (base !== 'deductibleWarranty' || submission.isDeductibleWarranty)
  ) {
    const additional = {}

    Object.keys(additional_coverages).forEach(function (key) {
      additional[additional_coverages[key]] = {
        selected: true,
      }
    })

    defaultCoverages = {
      gl: {
        base,
        additional,
      },
    }
  } else if (type === 'oi') {
    let key = 'default'

    defaultCoverages = {
      ny: {
        gl: {
          base: 'standard',
        },
      },
      default: {
        gl: {
          base: 'conditional',
        },
      },
    }

    if (projectState.toLowerCase() === 'new york') {
      key = 'ny'
    }

    defaultCoverages = defaultCoverages[key]
  } else if (type === 'ocp') {
    defaultCoverages = {
      gl: {
        base: 'standard',
      },
    }
  }
  // Set default optional coverage, meaning default it to selected if applicable.
  const applyDefaultOptionalCoverage = (optionalCovg) => {
    if (isNewSubmission) return true
    return false
  }
  if (defaultOptionalCoverages.length > 0) {
    defaultOptionalCoverages.forEach((optCovg) => {
      if (applyDefaultOptionalCoverage(optCovg.name)) {
        // if defaultStates array is undefined then assume coverage is CountryWide and applies.
        let coverageAppliesToState = optCovg.defaultStates === undefined ? true : false
        if (Array.isArray(optCovg.defaultStates)) {
          for (const state of optCovg.defaultStates) {
            if (state.toLowerCase() === projectState.toLowerCase()) {
              coverageAppliesToState = true
              break
            }
          }
        }

        defaultCoverages = {
          ...defaultCoverages,
          gl: {
            ...defaultCoverages.gl,
            optional: {
              ...defaultCoverages.gl.optional,
              [optCovg.name]: {
                selected: coverageAppliesToState,
              },
            },
          },
        }
      }
    })
  }
  if (isNewSubmission) {
    const additionalCoverages = createAdditionalCoverageSelection(
      defaultAdditionalCoverages,
    )
    defaultCoverages.gl.additional = Object.assign(
      {},
      defaultCoverages.gl.additional,
      additionalCoverages,
    )

    // Set default Fees for new Submissions
    if (defaultCoverages.gl.base === 'deductibleWarranty' ||
      submission.coverages?.gl?.base === 'deductibleWarranty'
    ) {
      const inspectionFeeDefaultData = defaultOIFees.find((feeDefaultData) => {
        return feeDefaultData.key === 'inspectionFee'
      })
      if (inspectionFeeDefaultData) {
        defaultCoverages.gl.fees = Object.assign(
          {},
          defaultCoverages.gl.fees,
          createGenericCoverageSelection([inspectionFeeDefaultData.key]),
        )
      }
    }
  }
  return Object.assign({}, defaultCoverages, submission.coverages)
}

/**
 * Constructs additional coverage selection object
 *
 * To avoid filtering of options (selected/not selected) at latter time,
 * this func. should be used
 *
 * @param {Object} additionalCoverage
 * @returns {} Object
 */

const DEFAULT_ADDITIONAL_COVERAGES = {
  oi: {
    identityRecovery: {
      selected: true,
    },
    terror: {
      selected: true,
    },
  },
  ocp: {
    terror: {
      selected: true,
    },
  },
  default: {},
}

exports.getAdditionalCoverageSelection = (
  additionalCoverage = {},
  type = 'default',
  rest,
) => {
  const additionalCoverage_ = Object.assign({}, additionalCoverage)
  if (
    !!rest &&
    rest.additionalInsured === 'true' &&
    additionalCoverage_.additionalInsureds === undefined
  ) {
    Object.assign(additionalCoverage_, { additionalInsureds: { selected: true } })
  }
  if (
    !!rest &&
    (rest.isBankSelected === true || rest.isBankSelected === undefined) &&
    additionalCoverage_.bankPremium === undefined
  ) {
    Object.assign(additionalCoverage_, { bankPremium: { selected: true } })
  }
  let result = {}
  const keys = Object.keys(additionalCoverage_)

  if (!additionalCoverage || !keys.length) {
    return DEFAULT_ADDITIONAL_COVERAGES[type]
  }

  result = keys.sort().reduce((acc, cur, idx) => {
    if (additionalCoverage_[cur].selected === true) {
      acc[cur] = {
        selected: true,
      }
    }
    return acc
  }, {})

  return result
}

/**
 * Creates additional coverage selection from array
 * @param {Array} values
 * @returns {} Object
 */
const createAdditionalCoverageSelection = (exports.createAdditionalCoverageSelection = (
  values,
) => {
  if (!Array.isArray(values)) {
    return {}
  }
  return values.sort().reduce((acc, cur, idx) => {
    acc[cur] = {
      selected: true,
    }
    return acc
  }, {})
})

/**
 * Creates a generic coverage selection from array
 * @param {Array} values
 * @returns {} Object
 */
const createGenericCoverageSelection = (
  values,
) => {
  if (!Array.isArray(values)) {
    return {}
  }
  return values.sort().reduce((acc, cur, idx) => {
    acc[cur] = {
      selected: true,
    }
    return acc
  }, {})
}

exports.getFeesCoverageSelection = (
  feesSelection = {}
) => {
  const feesSelectionCopy = {...feesSelection}

  let result = {}
  const keys = Object.keys(feesSelectionCopy)

  result = keys.sort().reduce((acc, cur, idx) => {
    if (feesSelectionCopy[cur].selected === true) {
      acc[cur] = {
        selected: true,
      }
    }
    return acc
  }, {})

  return result
}

/**
 * Constructs optional coverage selection object
 *
 * To avoid filtering of options (selected/not selected) at latter time,
 * this func. should be used
 *
 * @param {Object} optionalCoverage
 * @returns {} Object
 */

exports.getOptionalCoverageSelection = (optionalCoverage = {}, type = 'default') => {
  let result = {}
  const keys = Object.keys(optionalCoverage)

  result = keys.sort().reduce((acc, cur, idx) => {
    if (optionalCoverage[cur].selected === true) {
      acc[cur] = {
        selected: true,
      }
    }
    return acc
  }, {})

  return result
}

// /**
//  * Creates optional coverage selection from array
//  * @param {Array} values
//  * @returns {} Object
//  */
exports.createOptionalCoverageSelection = (values) => {
  if (!Array.isArray(values)) {
    return {}
  }
  return values.sort().reduce((acc, cur) => {
    acc[cur] = {
      selected: true,
    }
    return acc
  }, {})
}

/**
 * Check if an optional coverage is in the defaultOptionalCoverages array and whether it is
 * selected by default.  If coverage is not in the defaultOptionalCoverages return undefined
 * @param {string} optionalCoverageName
 * @param {Object} submission
 * @param {Object[]} defaultOptionalCoverages
 */
exports.getOptionalCoverageDefault = (
  optionalCoverageName,
  submission,
  defaultOptionalCoverages = [],
) => {
  const { projectAddress = {} } = submission
  const { projectState = '' } = projectAddress
  const optCovgDefaultItem = defaultOptionalCoverages.find((covg) => {
    return covg.name === optionalCoverageName
  })
  if (optCovgDefaultItem === undefined) return undefined
  // if defaultStates array is undefined then assume coverage is CountryWide and applies.
  let coverageSelected = optCovgDefaultItem.defaultStates === undefined ? true : false
  if (Array.isArray(optCovgDefaultItem.defaultStates)) {
    for (const defaultState of optCovgDefaultItem.defaultStates) {
      if (defaultState.toLowerCase() === projectState.toLowerCase()) {
        coverageSelected = true
        break
      }
    }
  }
  return coverageSelected
}

/**
 * @description The purpose of this function is to generate fee selection data
 * similarly to how a user generates it in the FeeSelector component. This is basically
 * a util function that can be used when switching Base Coverages and the system needs to
 * reset the default Fee selection. In this case the logic needs to generate this data 
 * rather than the user.
 * 
 * @param {*} baseCoverage 
 * @param {*} type
 */
exports.generateDefaultFeeData = (
  baseCoverage,
  type
) => {
  const defaultedFees = []
  // Set default Fees per Base Coverage
  if (type === 'oi') {
    if (baseCoverage === 'deductibleWarranty') {
      const inspectionFeeDefaultData = defaultOIFees.find((feeDefaultData) => {
        return feeDefaultData.key === 'inspectionFee'
      })
      if (inspectionFeeDefaultData) {
        defaultedFees.push(inspectionFeeDefaultData.key)
      }
    }
  }
  return defaultedFees
}

/**
 * Determines the project length based on the anticipated start and finish dates of the
 * specified submission.
 * @param {anticipatedStartDate}  the submission object
 * @param {anticipatedFinishDate}  the submission object
 */
exports.getProjectLength = (anticipatedStartDate, anticipatedFinishDate) => {
  return Math.ceil(
    moment(anticipatedFinishDate).diff(moment(anticipatedStartDate), 'months', true),
  )
}

/**
 * Returns the EDGE number from the related entities in submission object
 * @param {String} edgeNumber
 */
exports.getEdgeNumber = (submission) => {
  if (submission.relatedEntities && submission.relatedEntities.identifiers) {
    const identifier = submission.relatedEntities.identifiers.find(
      (identifier) => identifier.type === 'edge',
    )
    return identifier && identifier.id
  }
  return undefined
}

/**
 * Returns the Preferences with base_coverage and additional coverage
 * @param {Object} submission
 */
exports.setPreferences = (submission) => {
  const { base: base_coverage, additional } = submission.coverages.gl
  const additional_coverages = additional
    ? Object.keys(additional).filter((key) => {
        return additional[key].selected !== false
      })
    : {}
  return {
    id: submission.userId,
    product_defaults: {
      [submission.type]: {
        base_coverage,
        additional_coverages,
      },
    },
  }
}

exports.setCommission = (user) =>
  String(isUnderwriter(user) ? BASE_COMMISSION : user.brokerage.commission)

exports.getSubmissionFormParams = (submission) => {
  const submissionFormParams = {
    primaryInsuredName: { disabled: true },
    primaryInsuredAddress: { disabled: true },
    primaryInsuredAddress2: { disabled: true },
    primaryInsuredCity: { disabled: true },
    primaryInsuredState: { disabled: true },
    primaryInsuredZipcode: { disabled: true },
    projectAddress: { disabled: true },
    projectAddress2: { disabled: true },
    projectCity: { disabled: true },
    projectState: { disabled: true },
    projectZipcode: { disabled: true },
  }

  if (
    submission.type === 'ocp' &&
    submission.projectAddress.projectState !== 'New York'
  ) {
    submissionFormParams['nycha'] = { disabled: true }
  }

  return submissionFormParams
}

exports.hasExpired = (updatedAt, days = 90) => {
  const updatedTimeStamp = Math.round(Date.parse(updatedAt)) + days * 24 * 60 * 60 * 1000
  const currentTimeStamp = new Date().getTime()
  return currentTimeStamp > updatedTimeStamp
}

exports.resetPricingValues = (inputValues) => {
  const pricingValues = {}
  pricingValues.baseCoverageOption = inputValues.baseCoverageOption
  Object.keys(pricingValues.baseCoverageOption).forEach((key) => {
    pricingValues.baseCoverageOption[key] = 0
  })
  pricingValues.basePremium = 0
  return pricingValues
}

exports.setInitialPricingValues = (
  ratings,
  submission,
  selectedValue = '',
  isUnderwriter = false,
) => {
  let pricingValues = {}
  let baseCoverages = {}
  const { type, manuallySetPricing } = submission
  const nonSelectedOptions = difference(
    ['standard', 'conditional', 'biExcluded', 'deductibleWarranty'],
    [selectedValue],
  )

  if (manuallySetPricing === 'true') {
    pricingValues = submission.ratings[type]
    if (pricingValues.baseCoverageOption) {
      baseCoverages = pricingValues.baseCoverageOption
        ? pricingValues.baseCoverageOption
        : submission.ratings['options'].baseCoverageOption
    }
    const newBaseCoverageOption = {}
    nonSelectedOptions.forEach((option) => {
      Object.assign(newBaseCoverageOption, { [option]: Number(baseCoverages[option]) })
    })
    pricingValues.baseCoverageOption = newBaseCoverageOption
  }
  if (!ratings) {
    // Knockout case
    const typeRatings = submission.ratings ? submission.ratings[type] : {}
    const { baseCoverageOption = {}, basePremium = 0 } = typeRatings
    const newBaseCoverageOption = {}
    if (manuallySetPricing !== 'true' && !isUnderwriter) {
      nonSelectedOptions.forEach((option) => {
        Object.assign(newBaseCoverageOption, { [option]: 0 })
      })
    } else {
      nonSelectedOptions.forEach((option) => {
        Object.assign(newBaseCoverageOption, {
          [option]: baseCoverageOption[option] ? Number(baseCoverageOption[option]) : 0,
        })
      })
    }
    pricingValues.baseCoverageOption = newBaseCoverageOption
    pricingValues.basePremium = manuallySetPricing === 'true' ? basePremium : 0
  } else if (!manuallySetPricing || manuallySetPricing === 'false') {
    pricingValues = ratings.items[0]['options']
    const newBaseCoverageOption = {}
    baseCoverages = pricingValues.baseCoverageOption
      ? pricingValues.baseCoverageOption
      : {}
    nonSelectedOptions.forEach((option) => {
      if (baseCoverages[option]) {
        Object.assign(newBaseCoverageOption, { [option]: Number(baseCoverages[option]) })
      }
    })
    pricingValues.baseCoverageOption = newBaseCoverageOption
  }

  return pricingValues
}

/**
 * This updates pricingValue with new baseCoverageOption and basePremium based on new base coverage selected by user
 * @param {Object} pricingValues
 * @param {Object} selectedValue
 */
exports.setBaseCoverageInPricingValues = (pricingValues, selectedValue) => {
  let baseCoverageOption = pricingValues.baseCoverageOption
  if (Object.keys(baseCoverageOption).includes(selectedValue)) {
    const oldBasePremium = pricingValues.basePremium
    pricingValues.basePremium = baseCoverageOption[selectedValue]
    const oldSelectedValue = difference(
      ['standard', 'conditional', 'biExcluded', 'deductibleWarranty'],
      Object.keys(baseCoverageOption),
    )[0]
    baseCoverageOption = omit(baseCoverageOption, [selectedValue])
    if (oldSelectedValue) {
      baseCoverageOption[oldSelectedValue] = oldBasePremium
    }
    pricingValues.baseCoverageOption = baseCoverageOption
  }
  return pricingValues
}

const createBaseCoveragesForPricingValues = (ratingValues, selectedBaseCovg = '') => {
  let baseCoverages = {}
  const nonSelectedOptions = difference(
    ['standard', 'conditional', 'biExcluded', 'deductibleWarranty'],
    [selectedBaseCovg],
  )

  const newBaseCoverageOption = {}
  baseCoverages = ratingValues.baseCoverageOption ? ratingValues.baseCoverageOption : {}
  nonSelectedOptions.forEach((option) => {
    if (baseCoverages[option]) {
      Object.assign(newBaseCoverageOption, { [option]: Number(baseCoverages[option]) })
    }
  })
  return newBaseCoverageOption
}

/**
 * This function replaces the entire pricingValues object when:
 *  1. Base Coverage changes
 *  2. User switches from System Generated Pricing to Manual
 *  3. Or user is a broker, in which case they shouldn't have manual premiums
 *
 * If none of the above conditions are met, individual premiums will be updated
 *
 * @param {Object} ratings
 * @param {Object} prevRatings
 * @param {Object} submission
 * @param {Object} pricingValues
 * @param {string} selectedBaseCovg
 * @param {string[]} userActions
 * @param {Boolean} isUnderwriter
 * @param {Object} additionalCoverage
 * @param {string} excessLimitAmount
 */
exports.updatePricingValuesFromRatings = (
  ratings,
  prevRatings,
  submission,
  pricingValues,
  selectedBaseCovg = '',
  userActions,
  isUnderwriter,
  additionalCoverage,
  excessLimitAmount,
  feesSelection
) => {
  let newPricingValues
  let isPricingModified = false //set flag to true if data has changed

  /*
   * When systemBasePremium exists but is null, use this as a flag to replace the entire pricingValues
   * object. This is done when base coverage changes or user switches from System Generated to Manual Premium.
   */
  if (
    (pricingValues.hasOwnProperty('systemBasePremium') &&
      pricingValues.systemBasePremium == null) ||
    !isUnderwriter
  ) {
    newPricingValues = cloneDeep(ratings.items[0]['options'])
    const limits = cloneDeep(ratings.items[0][submission.type].limits)
    const systemLimits = cloneDeep(ratings.items[0][submission.type].limits)
    const rate = ratings.items[0][submission.type].rate
    const oeMinimumPremium = ratings.items[0][submission.type].oeMinimumPremium
    Object.assign(newPricingValues, { limits, systemLimits, rate, oeMinimumPremium })

    newPricingValues.baseCoverageOption = createBaseCoveragesForPricingValues(
      newPricingValues,
      selectedBaseCovg,
    )
    newPricingValues.systemBasePremium = newPricingValues.basePremium
    if (ratings.items[0][submission.type].additionalInsureds != null) {
      newPricingValues.additionalInsureds =
        ratings.items[0][submission.type].additionalInsureds
    }
    if (ratings.items[0][submission.type].bankPremium != null) {
      newPricingValues.bankPremium = ratings.items[0][submission.type].bankPremium
    }
    if (pricingValues.hasOwnProperty('u653Deductible')) {
      newPricingValues.u653Deductible = pricingValues.u653Deductible
    }

    return newPricingValues
  }

  newPricingValues = cloneDeep(pricingValues)

  // Update systemBasePremium and reset scheduleFactor and manualRate when systemBasePremium changes.
  let basePrem = ratings.items[0].options.baseCoverageOption[selectedBaseCovg]
  if (basePrem != null && basePrem !== '') {
    basePrem = Number(basePrem)
    let systemBasePrem = newPricingValues.systemBasePremium
    systemBasePrem =
      typeof systemBasePrem === 'string' ? Number(systemBasePrem) : systemBasePrem
    if (systemBasePrem !== basePrem) {
      newPricingValues.systemBasePremium = basePrem
      if (Object.hasOwn(newPricingValues, 'scheduleFactor')) {
        newPricingValues.scheduleFactor = null
      }
      if (Object.hasOwn(newPricingValues, 'manualRate')) {
        newPricingValues.manualRate = null
      }
      isPricingModified = true
    }
  }

  // Update basePremium if it changes
  if (
    (ratings.items[0].options.basePremium !== prevRatings.items[0].options.basePremium &&
      newPricingValues.basePremium === prevRatings.items[0].options.basePremium) ||
    ((userActions.includes(userActionsConstants.COMMISSION_CHANGED) ||
      userActions.includes(userActionsConstants.LIMITS_CHANGED)) &&
      ratings.items[0].options.basePremium !== prevRatings.items[0].options.basePremium)
  ) {
    newPricingValues.basePremium = ratings.items[0].options.basePremium
    isPricingModified = true
  }

  // Always get the latest baseCoverages because they may change.
  newPricingValues.baseCoverageOption = createBaseCoveragesForPricingValues(
    ratings.items[0].options,
    selectedBaseCovg,
  )

  const { type } = submission

  let additionalCoveragesList = []
  if (type === 'oi') {
    additionalCoveragesList = oiDefaultCoverages
  } else if (type === 'ocp') {
    additionalCoveragesList = ocpDefaultCoverages
  }

  /*
   * Set the newPricingValues Additional Coverage Premium.
   *
   * Conditional logic is setting the Additional Coverage premium based on these 2 conditions:
   * 1. If the Additional Coverage system (option) premium changes then set Additional
   * Coverage premium to new system (option) amount.
   *
   * 2. If the Manual Base premium is updated and it doesn't equal the System Base Premium
   *    then set the Additional Coverage premium to the system (option) amount.
   */
  const getUpdatedCoverages = (key, value) => {
    if (
      type === 'ocp' &&
      userActions.includes(userActionsConstants.LIMITS_CHANGED) &&
      ratings.items[0].options.hasOwnProperty(key)
    ) {
      newPricingValues[key] = ratings.items[0].options[key]
      isPricingModified = true
    } else if (key === 'bankPremium') {
      /*
       * bankPremium is not contained under ratings.options so update it to system
       * generated amount when user user selects or deselects it.
       */
      if (
        userActions.includes(userActionsConstants.ADDL_COVG_LENDER_CHANGED) &&
        ratings.items[0][type].hasOwnProperty(key)
      ) {
        newPricingValues[key] = ratings.items[0][type][key]
        isPricingModified = true
      }
    } else if (key === 'additionalInsureds') {
      /*
       * Manually overriden Additional Insured premium is passed to CD so
       * it should be safe to always update pricingValues
       */
      if (
        newPricingValues.hasOwnProperty(key) &&
        ratings.items[0][type].hasOwnProperty(key) &&
        ratings.items[0][type][key] != null
      ) {
        newPricingValues[key] = ratings.items[0][type][key]
        isPricingModified = true
      }
    } else {
      /*
       * Update Additional Coverage premium if there is a new calculated value
       * and user hasn't manually modified it. If value in pricingValues
       * is different than value prevRating, then user has modified it.
       */
      if (
        prevRatings.items[0].options[key] !== ratings.items[0].options[key] &&
        newPricingValues[key] === prevRatings.items[0].options[key] &&
        ratings.items[0].options.hasOwnProperty(key)
      ) {
        newPricingValues[key] = ratings.items[0].options[key]
        isPricingModified = true
      } else if (
        !additionalCoverage.hasOwnProperty(value) &&
        newPricingValues.hasOwnProperty(key) &&
        ratings.items[0][type].hasOwnProperty(key) &&
        ratings.items[0][type][key] === 0 &&
        newPricingValues[key] !== ratings.items[0].options[key]
      ) {
        // user has deselected Additional Coverage so set to CD value.
        newPricingValues[key] = ratings.items[0].options[key]
        isPricingModified = true
      }
    }
  }

  for (const covg of additionalCoveragesList) {
    if (Array.isArray(covg.key)) {
      for (const k of covg.key) {
        getUpdatedCoverages(k, covg.value)
      }
    } else {
      getUpdatedCoverages(covg.key, covg.value)
    }
  }

  let triaSelected = false
  if (
    submission.coverages.gl.additional.hasOwnProperty('terror') &&
    submission.coverages.gl.additional.terror.selected === true
  ) {
    triaSelected = true
  }

  //update pricingValues with updated excess data
  if (
    ((prevRatings.items[0].options.excessPremium !==
      ratings.items[0].options.excessPremium &&
      newPricingValues.excessPremium === prevRatings.items[0].options.excessPremium) ||
      excessLimitAmount === 'noExcess' ||
      userActions.includes(userActionsConstants.LIMITS_CHANGED)) &&
    newPricingValues.excessPremium !== ratings.items[0].options.excessPremium
  ) {
    newPricingValues.excessPremium = ratings.items[0].options.excessPremium
    isPricingModified = true
  }
  if (
    ((prevRatings.items[0].options.excessTerrorPremium !==
      ratings.items[0].options.excessTerrorPremium &&
      newPricingValues.excessTerrorPremium ===
        prevRatings.items[0].options.excessTerrorPremium) ||
      excessLimitAmount === 'noExcess' ||
      triaSelected === false ||
      userActions.includes(userActionsConstants.LIMITS_CHANGED)) &&
    newPricingValues.excessTerrorPremium !== ratings.items[0].options.excessTerrorPremium
  ) {
    newPricingValues.excessTerrorPremium = ratings.items[0].options.excessTerrorPremium
    isPricingModified = true
  }

  // update limits
  const limitTypeKey = type === 'oi' ? 'u658' : 'ocp008a'
  if (userActions.includes(userActionsConstants.LIMITS_CHANGED)) {
    //update GC Limit data
    const newLimits = ratings.items[0][type].limits[limitTypeKey]

    for (const key in newLimits) {
      if (newLimits[key] != null) {
        newPricingValues.limits[limitTypeKey][key] = newLimits[key]
        isPricingModified = true
      }
    }
  } else {
    /**
     * Update limits if Rating returns new limit and it has not been updated by the user. In order to
     * check if a user has not manually updated the limits, we need to check the systemLimits in the
     * newPricingValues object. The systemLimits will be set to the previously set systemLimits.
     */
    const newLimits = ratings.items[0][type].limits &&
      ratings.items[0][type].limits[limitTypeKey] ?
      ratings.items[0][type].limits[limitTypeKey] :
      {}
    const newPricingValueSystemLimits = newPricingValues.systemLimits &&
      newPricingValues.systemLimits[limitTypeKey] ?
      newPricingValues.systemLimits[limitTypeKey] :
      {}
    const newPricingValueLimits = newPricingValues.limits && newPricingValues.limits[limitTypeKey] ?
      newPricingValues.limits[limitTypeKey] :
      {}
    for (const key in newLimits) {
      if (newLimits[key] !== newPricingValueSystemLimits[key] && newPricingValueLimits[key] === newPricingValueSystemLimits[key]) {
        newPricingValueLimits[key] = newLimits[key]
        isPricingModified = true
      }
    }
  }

  /**
   * Set systemLimits after updating limits in newPricingLimits since the updating limits needs to 
   * look at the previous systemLimits.
   */
  if (!Object.hasOwn(newPricingValues, 'systemLimits')) {
    newPricingValues.systemLimits = cloneDeep(ratings.items[0][type].limits)
    isPricingModified = true
  } else {
    if (
      ratings.items[0][type].limits &&
      JSON.stringify(ratings.items[0][type].limits) !==
        JSON.stringify(newPricingValues.systemLimits)
    ) {
      newPricingValues.systemLimits = cloneDeep(ratings.items[0][type].limits)
      isPricingModified = true
    }
  }

  // copy surcharge if it is in the current ratings but missing from pricing values.
  if (
    Object.hasOwn(ratings.items[0].options, 'surcharge') &&
    newPricingValues.surcharge !== ratings.items[0].options.surcharge
  ) {
    newPricingValues.surcharge = ratings.items[0].options.surcharge
    isPricingModified = true
  }

  /**
   * Update Inspection Fee if it changes and the user hasn't manually updated it. This
   * can happen when an existing submission doesn't have the fee in the pricingValues but
   * Rating is returning the value. Or update the Inspection Fee when it has been de-selected
   * and Rating is returning $0.
   * 
   * If we ever add additional Inspection Fees you could convert this logic to iterate
   * thru the default fees to make this logic generic enough to accomodate all the fees.
   * See the Additional Coverage logic.
   */
  if (
    prevRatings.items[0].options.inspectionFee !== ratings.items[0].options.inspectionFee &&
    newPricingValues.inspectionFee === prevRatings.items[0].options.inspectionFee &&
    ratings.items[0].options.hasOwnProperty('inspectionFee')
  ) {
    newPricingValues.inspectionFee = ratings.items[0].options.inspectionFee
    isPricingModified = true
  } else if (
    !Object.hasOwn(feesSelection, 'inspectionFee') &&
    Object.hasOwn(newPricingValues, 'inspectionFee') &&
    Object.hasOwn(ratings.items[0][type], 'inspectionFee') &&
    ratings.items[0][type].inspectionFee === 0 &&
    newPricingValues.inspectionFee !== ratings.items[0].options.inspectionFee
  ) {
    // user has deselected Inspection Fee so set to CD value.
    newPricingValues.inspectionFee = ratings.items[0].options.inspectionFee
    isPricingModified = true
  }

  // copy systemRateVersion if it is in the current ratings but missing from pricing values.
  if (
    Object.hasOwn(ratings.items[0].options, 'systemRateVersion') &&
    newPricingValues.systemRateVersion !== ratings.items[0].options.systemRateVersion
  ) {
    newPricingValues.systemRateVersion = ratings.items[0].options.systemRateVersion
    isPricingModified = true
  }

  // copy rate from type ratings if it doesn't match pricingValues rate.
  if (Object.hasOwn(ratings.items[0][type], 'rate') && newPricingValues.rate !== ratings.items[0][type].rate) {
    newPricingValues.rate = ratings.items[0][type].rate;
    isPricingModified = true;
  }

  // copy oeMinimumPremium from type ratings if it doesn't match pricingValues oeMinimumPremium.
  if (Object.hasOwn(ratings.items[0][type], 'oeMinimumPremium') && newPricingValues.oeMinimumPremium !== ratings.items[0][type].oeMinimumPremium) {
    newPricingValues.oeMinimumPremium = ratings.items[0][type].oeMinimumPremium;
    isPricingModified = true;
  }

  // null manualRate if the basePremium is at or below oeMinimumPremium
  if (Object.hasOwn(ratings.items[0][type], 'oeMinimumPremium') && newPricingValues.basePremium <= ratings.items[0][type].oeMinimumPremium) {
    newPricingValues.manualRate = null;
    isPricingModified = true;
  }

  if (isPricingModified) return newPricingValues

  return null
}

exports.updateRelatedEntitiesWithEdgeNumber = (relatedEntities = {}, edgeNumber) => {
  const edgeNumberIdentifier = { id: edgeNumber, type: 'edge' }
  if (Array.isArray(relatedEntities.identifiers) && relatedEntities.identifiers.length) {
    if (!edgeNumber.length) {
      // Clear the edge number
      relatedEntities.identifiers = relatedEntities.identifiers.filter(
        (element) => element.type !== 'edge',
      )
    } else {
      // Find and replace the element in identifiers
      relatedEntities.identifiers = relatedEntities.identifiers.map((element) =>
        element.type === 'edge' ? edgeNumberIdentifier : element,
      )
    }
  } else {
    // Add elemement in identifiers when identifiers is not present
    relatedEntities.identifiers = [edgeNumberIdentifier]
  }
  return relatedEntities
}

exports.getInitialPricingOptionsFields = (type) => {
  if (type === 'oi') {
    return [
      {
        key: 'totalPremium',
        required: true,
        label: 'Total Premium',
      },
      {
        key: 'limits.u658.occurenceLimit',
        required: true,
        label: 'Each Occurance',
      },
      {
        key: 'limits.u658.aggregateLimit',
        required: true,
        label: 'General Aggregate',
      },
      {
        key: 'limits.u658.completedOpsLimit',
        required: true,
        label: 'Product Completed Ops',
      },
    ]
  } else if (type === 'ocp') {
    return [
      {
        key: 'totalPremium',
        required: true,
        label: 'Total Premium',
      },
      {
        key: 'basePremium',
        required: true,
        label: 'Base Premium',
      },
      {
        key: 'limits.ocp008a.eachOccurrence',
        required: true,
        label: 'Each Occurance',
      },
      {
        key: 'limits.ocp008a.generalAggregate',
        required: true,
        label: 'General Aggregate',
      },
    ]
  }
}

exports.getRatingProperties = (submission) => {
  let props = pick(submission, ['type', 'totalCost', 'adjustedCommission'])
  props = Object.assign(props, {
    anticipatedProjectLength: exports.getProjectLength(
      submission.anticipatedStartDate,
      submission.anticipatedFinishDate,
    ),
    projectState: get(submission, 'projectAddress.projectState'),
    projectZipcode: get(submission, 'projectAddress.projectZipcode'),
  })

  const specProps = {
    oi: [
      'commission',
      'excessLimitAmount',
      'exteriorDemo',
      'isBankSelected',
      'numberOfBankForms',
      'numberOfAIForms',
      'occupancy',
      'totalCost',
      'type',
      'isVerticalExpansion',
      'exteriorWorkFiveStories',
      'isPCOpsExcluded',
      'isESWorkExcluded',
      'hnoaRequired',
      'anticipatedStartDate',
      'anticipatedFinishDate',
      'dateOfCreation',
      'projectType',
      'structuralWork',
      'eightFeet',
      'demolitionDetailsGCOwner',
      'occupancyDescription',
      'commonOwnership',
      'demolitionDetailsInterior',
      'generalLiabilityCarrier',
      'isHorizontalExpansion'
    ],
    ocp: [
      'commission',
      'contractorSameAllSites',
      'exteriorWorkFourStories',
      'limitsRequested',
      'nycha',
      'servicingSeveralLocations',
      'anticipatedStartDate',
      'anticipatedFinishDate',
      'dateOfCreation',
      'exteriorDemo',
      'isVerticalExpansion',
      'isHorizontalExpansion'
    ],
  }

  props = Object.assign(props, pick(submission, specProps[props.type]))
  const validateCreatedDate = moment(
    moment(submission.dateOfCreation).format('YYYY-MM-DD'),
  ).isAfter('2020-05-02')
  const validateEffDate = moment(
    moment(submission.anticipatedStartDate).format('YYYY-MM-DD'),
  ).isAfter('2020-05-02')
  if (validateEffDate && validateCreatedDate) {
    props.additionalCoverage = 'terror'
  }
  return props
}

exports.createBaseCoveragesForPricingValues = createBaseCoveragesForPricingValues
exports.createGenericCoverageSelection = createGenericCoverageSelection
